import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  ImpersonationSession,
  ImpersonationStartRequest,
  ImpersonationStartResponse,
  ImpersonationStopResponse,
  Logger,
  User
} from 'shared';

@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  protected logger = new Logger('ImpersonationService');
  private impersonationSession$ = new BehaviorSubject<ImpersonationSession>({ active: false });
  private impersonationToken: string | null = null;

  constructor(private http: HttpClient) {
    // Restore session from sessionStorage on init
    this.restoreSession();
  }

  /**
   * Start impersonating a user
   */
  startImpersonation(userId: number): Observable<User> {
    const request: ImpersonationStartRequest = { user_id: userId };

    return this.http.post<ImpersonationStartResponse>('/impersonate/start', request).pipe(
      tap(response => {
        if (response.success) {
          this.impersonationToken = response.impersonation_token;

          const session: ImpersonationSession = {
            active: true,
            impersonatedUserId: response.impersonated_user.id,
            impersonatedUser: response.impersonated_user,
            expiresAt: response.expires_at,
            token: response.impersonation_token
          };

          // Store in sessionStorage (survives refresh, not browser restart)
          sessionStorage.setItem('impersonation_session', JSON.stringify(session));

          this.impersonationSession$.next(session);
        }
      }),
      map(response => response.impersonated_user)
    );
  }

  /**
   * Stop impersonating and return to admin session
   */
  stopImpersonation(): Observable<void> {
    return this.http.post<ImpersonationStopResponse>('/impersonate/stop', {}).pipe(
      tap(() => {
        this.clearSession();
      }),
      map(() => void 0)
    );
  }

  /**
   * Check impersonation status from backend
   */
  checkStatus(): Observable<ImpersonationSession> {
    return this.http.get<ImpersonationSession>('/impersonate/status').pipe(
      tap(session => {
        if (session.active) {
          // Update local state with server state
          const storedSession: ImpersonationSession = {
            ...session,
            token: this.impersonationToken || session.token || ''
          };
          this.impersonationSession$.next(storedSession);
          sessionStorage.setItem('impersonation_session', JSON.stringify(storedSession));
        } else {
          this.clearSession();
        }
      })
    );
  }

  /**
   * Get current impersonation session as observable
   */
  getSession$(): Observable<ImpersonationSession> {
    return this.impersonationSession$.asObservable();
  }

  /**
   * Get current impersonation session
   */
  getSession(): ImpersonationSession {
    return this.impersonationSession$.value;
  }

  /**
   * Get impersonation token for HTTP headers
   */
  getToken(): string | null {
    return this.impersonationToken;
  }

  /**
   * Check if currently impersonating
   */
  isImpersonating(): boolean {
    return this.impersonationSession$.value.active;
  }

  /**
   * Clear impersonation session
   */
  clearSession(): void {
    this.impersonationToken = null;
    sessionStorage.removeItem('impersonation_session');
    this.impersonationSession$.next({ active: false });
  }

  /**
   * Restore impersonation session from sessionStorage
   */
  private restoreSession(): void {
    const stored = sessionStorage.getItem('impersonation_session');
    if (stored) {
      try {
        const session: ImpersonationSession = JSON.parse(stored);

        // Check if session expired
        if (session.expiresAt && new Date(session.expiresAt) > new Date()) {
          this.impersonationToken = session.token || null;
          this.impersonationSession$.next(session);
        } else {
          this.clearSession();
        }
      } catch (e) {
        this.logger.error('Failed to restore impersonation session', e);
        this.clearSession();
      }
    }
  }
}
